import React, { useState, useEffect } from 'react';
import { FaRupeeSign, FaDownload, FaRedo, FaPercentage } from 'react-icons/fa';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { formatCurrency } from '../utils/calculations';
import { generateGenericPDF } from '../utils/pdfGenerator';
import SEO from '../components/SEO';
import { isPrerender } from '../utils/prerender';

ChartJS.register(ArcElement, Tooltip, Legend);

const CAGRCalculator = () => {
  const shouldRenderChart = !isPrerender();
  const [initialValue, setInitialValue] = useState(100000);
  const [finalValue, setFinalValue] = useState(200000);
  const [years, setYears] = useState(5);
  const [result, setResult] = useState(null);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    calculate();
  }, [initialValue, finalValue, years]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const calculate = () => {
    const initVal = Number(initialValue) || 0;
    const finVal = Number(finalValue) || 0;
    const yrs = Number(years) || 0;
    if (yrs <= 0 || initVal <= 0) {
      setResult(null);
      return;
    }

    // CAGR = ((Final Value / Initial Value) ^ (1/n)) - 1
    const cagr = (Math.pow(finVal / initVal, 1 / yrs) - 1) * 100;
    const absoluteReturn = ((finVal - initVal) / initVal) * 100;
    const totalGain = finVal - initVal;
    
    // Generate year-wise growth at CAGR
    const yearlyData = [];
    let value = initVal;
    for (let year = 0; year <= yrs; year++) {
      yearlyData.push({
        year,
        value: Math.round(value),
      });
      value = value * (1 + cagr / 100);
    }

    setResult({
      cagr,
      absoluteReturn,
      totalGain,
      yearlyData,
      multiplier: (finVal / initVal).toFixed(2),
    });
  };

  const handleDownloadPDF = () => {
    if (!result) return;
    generateGenericPDF('CAGR Calculator Report', {
      'Initial Investment': formatCurrency(initialValue),
      'Final Value': formatCurrency(finalValue),
      'Time Period': `${years} years`,
      'CAGR': `${result.cagr.toFixed(2)}%`,
      'Absolute Return': `${result.absoluteReturn.toFixed(2)}%`,
      'Total Gain': formatCurrency(result.totalGain),
      'Multiplier': `${result.multiplier}x`,
    });
  };

  const handleReset = () => {
    setInitialValue(100000);
    setFinalValue(200000);
    setYears(5);
  };

  const chartData = result ? {
    labels: ['Initial Investment', 'Gain'],
    datasets: [{
      data: [initialValue, result.totalGain],
      backgroundColor: ['#3b82f6', '#10b981'],
      borderWidth: 0,
    }],
  } : null;

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'bottom' },
    },
  };

  return (
    <div className="calculator-page fade-in">
      <SEO 
        title="CAGR Calculator"
        description="Free CAGR Calculator - Calculate Compound Annual Growth Rate for investments."
        keywords="CAGR calculator, compound annual growth rate calculator, CAGR formula, investment growth calculator, annualized return calculator"
        canonicalPath="/cagr"
      />
      <div className="calculator-header">
        <h1><FaPercentage style={{ color: '#8b5cf6' }} /> CAGR Calculator</h1>
        <p>Calculate Compound Annual Growth Rate of your investments</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Initial Investment (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={initialValue === '' ? '' : initialValue}
                onChange={(e) => setInitialValue(e.target.value === '' ? '' : Number(e.target.value))}
                min="1"
              />
            </div>
            <div className="slider-container">
              <input
                type="range"
                min="1000"
                max="10000000"
                step="1000"
                value={initialValue || 0}
                onChange={(e) => setInitialValue(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
          </div>

          <div className="input-group">
            <label>Final Value (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={finalValue === '' ? '' : finalValue}
                onChange={(e) => setFinalValue(e.target.value === '' ? '' : Number(e.target.value))}
                min="1"
              />
            </div>
            <div className="slider-container">
              <input
                type="range"
                min="1000"
                max="50000000"
                step="1000"
                value={finalValue || 0}
                onChange={(e) => setFinalValue(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
          </div>

          <div className="input-group">
            <label>Time Period (Years)</label>
            <input
              type="number"
              value={years === '' ? '' : years}
              onChange={(e) => setYears(e.target.value === '' ? '' : Number(e.target.value))}
              min="1"
              max="50"
            />
            <div className="slider-container">
              <input
                type="range"
                min="1"
                max="30"
                value={years || 0}
                onChange={(e) => setYears(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
          </div>

          <div className="button-group">
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          {result && (
            <>
              <div className="result-summary" style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' }}>
                <h3>CAGR (Compound Annual Growth Rate)</h3>
                <div className="amount">{result.cagr.toFixed(2)}%</div>
                <small>Your investment grew {result.multiplier}x</small>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                <div className="result-item" style={{ background: '#eff6ff', textAlign: 'center', padding: '16px' }}>
                  <span className="label">Absolute Return</span>
                  <span className="value" style={{ color: '#2563eb' }}>{result.absoluteReturn.toFixed(2)}%</span>
                </div>
                <div className="result-item" style={{ background: '#f0fdf4', textAlign: 'center', padding: '16px' }}>
                  <span className="label">Total Gain</span>
                  <span className="value" style={{ color: '#16a34a' }}>{formatCurrency(result.totalGain)}</span>
                </div>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Initial Investment</span>
                  <span className="value">{formatCurrency(initialValue)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Final Value</span>
                  <span className="value" style={{ color: '#10b981' }}>{formatCurrency(finalValue)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Investment Period</span>
                  <span className="value">{years} years</span>
                </div>
              </div>

              {/* Chart */}
              {shouldRenderChart && chartData && (
                <div style={{ maxWidth: '250px', margin: '20px auto' }}>
                  <Pie data={chartData} options={chartOptions} />
                </div>
              )}

              {/* Year-wise Growth */}
              <div className="breakup-section">
                <h4>Year-wise Value at {result.cagr.toFixed(2)}% CAGR</h4>
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  <table className="tax-slab-table">
                    <thead>
                      <tr>
                        <th>Year</th>
                        <th>Value</th>
                        <th>Growth</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.yearlyData.map((row, index) => (
                        <tr key={index}>
                          <td>{row.year}</td>
                          <td style={{ fontWeight: 'bold' }}>{formatCurrency(row.value)}</td>
                          <td style={{ color: '#10b981' }}>
                            {row.year === 0 ? '-' : `+${((row.value / result.yearlyData[0].value - 1) * 100).toFixed(1)}%`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>📌 Understanding CAGR</h4>
        <div style={{ marginTop: '12px' }}>
          <p><strong>CAGR Formula:</strong> ((Final Value / Initial Value) ^ (1/n)) - 1</p>
          <ul style={{ marginTop: '10px' }}>
            <li><strong>CAGR</strong> shows smoothed annual growth rate assuming reinvestment</li>
            <li><strong>Absolute Return</strong> = Total percentage gain over entire period</li>
            <li>CAGR is useful for comparing different investments over different time periods</li>
            <li>It doesn't reflect actual year-to-year volatility</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CAGRCalculator;
